/**
 * AiChatMessages — Scrollable message list with auto-scroll to bottom.
 */

import { useEffect, useRef } from 'react';
import { MessageSquare, History } from 'lucide-react';
import type { AiChatMessage as AiChatMessageType, AiChatPhase, AiChatRunMetadata } from '../types';
import { AiChatMessage } from './AiChatMessage';
import { AiChatSessionMetadata } from './AiChatSessionMetadata';

type AiChatMessagesProps = {
  messages: AiChatMessageType[];
  isLoading: boolean;
  showResumeNotice?: boolean;
  onStartFresh?: () => void;
  onLoadHistory?: () => void;
  isLoadingHistory?: boolean;
  statusText?: string | null;
  phase?: AiChatPhase;
  lastRunMetadata?: AiChatRunMetadata | null;
};

export function AiChatMessages({
  messages, isLoading, showResumeNotice, onStartFresh, onLoadHistory, isLoadingHistory,
  statusText, phase, lastRunMetadata,
}: AiChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  // Track if user scrolled up
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    userScrolledUpRef.current = !atBottom;
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (!userScrolledUpRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  if (messages.length === 0 && !showResumeNotice) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center text-muted-foreground px-4"
        data-testid="ai-chat-empty-state"
      >
        <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
        <p className="text-sm">Ask Claude about your SQL...</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
      onScroll={handleScroll}
      data-testid="ai-chat-messages"
    >
      {/* Resume notice */}
      {showResumeNotice && (
        <div
          className="flex flex-col gap-1.5 px-3 py-2 rounded-md bg-muted/50 text-xs text-muted-foreground"
          data-testid="ai-chat-resume-notice"
        >
          <div className="flex items-center gap-2">
            <History className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">Previous conversation will be resumed</span>
          </div>
          <div className="flex items-center gap-1.5 ml-5.5">
            {onLoadHistory && (
              <button
                className="px-2 py-0.5 rounded text-xs bg-primary/10 hover:bg-primary/20 text-primary transition-colors disabled:opacity-50"
                onClick={onLoadHistory}
                disabled={isLoadingHistory}
                data-testid="ai-chat-load-history"
              >
                {isLoadingHistory ? 'Loading...' : 'Load history'}
              </button>
            )}
            {onStartFresh && (
              <button
                className="px-2 py-0.5 rounded text-xs hover:bg-muted transition-colors"
                onClick={onStartFresh}
                data-testid="ai-chat-start-fresh"
              >
                Start fresh
              </button>
            )}
          </div>
        </div>
      )}

      {messages.map((msg, i) => (
        <AiChatMessage
          key={msg.id}
          message={msg}
          statusText={isLoading && i === messages.length - 1 && msg.role === 'assistant' ? (statusText || 'Thinking') : null}
        />
      ))}

      {/* Session metadata after completion */}
      {phase === 'completed' && lastRunMetadata && (
        <AiChatSessionMetadata metadata={lastRunMetadata} />
      )}

      <div ref={bottomRef} />
    </div>
  );
}
